import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import { VideoPlayer360Controller } from '../VideoPlayer360Controller'
import { Sphere360Handle } from '../surfaceTypes'
import type { PlaybackCore } from '../core/PlaybackCore'
import { SINGLE_PANE, type PaneLayout, type FourViewFraming } from '../splitLayout'
import styles from '../styles'

interface Sphere360SurfaceProps {
  // The persistent media pipeline. The surface never creates or disposes it —
  // it hands the core's element and player to the controller, which renders
  // that element as a sphere texture. Media events and transport belong to the
  // core, so none of them appear on this component.
  core: PlaybackCore
  // Camera restored from the URL, applied once right after the controller is
  // built. Pitch is view elevation in degrees (0 = horizon, negative looks
  // down) and is clamped to the active preset's polar limits.
  initialCamera?: { yaw?: number; pitch?: number }
  rotationAdjustment?: number
  // False in the admin's camera previews: no drag, wheel, pinch or motion
  // moves the camera, so what shows is exactly what the sliders set.
  interactive?: boolean
  fillHeight?: boolean
  compact?: boolean
  // Split screen: how many panes the sphere is drawn through (splitLayout.ts).
  // Locked, every pane faces `paneHeadings[i]`.
  layout?: PaneLayout
  paneHeadings?: number[]
  // The locked panes' panel shape and zoom (splitLayout.ts).
  fourView?: FourViewFraming
  // The two-pane layout's right camera, reported like the main one's.
  onSplitCameraUpdate?: (yaw: number, pitch: number, fov: number, fovVertical: number) => void
  onSplitViewPresetChange?: (preset: 'default' | 'topDown' | 'bottomUp') => void
  onCameraUpdate?: (yaw: number, pitch: number, fov: number, fovVertical: number) => void
  onViewPresetChange?: (preset: 'default' | 'topDown' | 'bottomUp') => void
  onGyroAvailabilityChange?: (available: boolean) => void
  onGyroStateChange?: (enabled: boolean) => void
  onFullscreenChange?: (full: boolean) => void
  setWrapper?: (el: HTMLElement | null) => void
  onContextLost?: () => void
}

const Sphere360Surface = forwardRef<Sphere360Handle, Sphere360SurfaceProps>((props, ref) => {
  const { core, initialCamera, rotationAdjustment, fillHeight, compact, layout = SINGLE_PANE, paneHeadings, fourView } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const controllerRef = useRef<VideoPlayer360Controller | null>(null)
  // Callbacks that outlive the mount effect (context loss) read the freshest
  // props through this ref so parent closures never go stale.
  const propsRef = useRef(props)
  propsRef.current = props
  const [showIOSPermission, setShowIOSPermission] = useState(false)
  const [iosPermissionDenied, setIOSPermissionDenied] = useState(false)
  const motionGrantedRef = useRef(false)

  useImperativeHandle(ref, (): Sphere360Handle => ({
    setViewPreset: (p, pane) => controllerRef.current?.setViewPreset(p, true, pane),
    rotateTo: (deg, animate, pane) => controllerRef.current?.rotateTo(deg, animate, pane),
    setCamera: (yaw, pitch) => controllerRef.current?.setCamera(yaw, pitch),
    setSpatialAudioEnabled: (on) => controllerRef.current?.setSpatialAudioEnabled(on),
    setActiveSpeaker: (pos) => controllerRef.current?.setActiveSpeaker(pos),
    // Where motion needs permission (iOS) and has not had it yet, turning it
    // on goes through our prompt first; everywhere else it just toggles.
    toggleGyro: () => {
      const controller = controllerRef.current
      if (!controller) return
      if (!controller.isGyroEnabled() && !motionGrantedRef.current && VideoPlayer360Controller.needsPermissionRequest()) {
        setShowIOSPermission(true)
        return
      }
      controller.toggleGyro()
    },
    setRotationAdjustment: (deg) => controllerRef.current?.setRotationAdjustment(deg),
    toggleFullscreen: () => controllerRef.current?.toggleFullscreen(),
    getVideoElement: () => core.videoElement,
    sampleFramePixels: () => controllerRef.current?.sampleFramePixels() ?? null,
    getZoomDiagnostics: () => controllerRef.current?.getZoomDiagnostics() ?? null,
  }), [core])

  // Build the renderer around the core's pipeline. Mount and unmount touch
  // presentation only — playback carries on across a view switch.
  useEffect(() => {
    if (!containerRef.current) return
    const controller = new VideoPlayer360Controller(
      containerRef.current,
      core.videoElement,
      core.player,
      core.spatialAudio,
      {
        onCameraUpdate: (y, p, f, fv) => propsRef.current.onCameraUpdate?.(y, p, f, fv),
        onViewPresetChange: (p) => propsRef.current.onViewPresetChange?.(p),
        onGyroStateChange: (e) => propsRef.current.onGyroStateChange?.(e),
        onFullscreenChange: (f) => propsRef.current.onFullscreenChange?.(f),
        onUserCameraInteraction: () => {},
        onSplitCameraUpdate: (y, p, f, fv) => propsRef.current.onSplitCameraUpdate?.(y, p, f, fv),
        onSplitViewPresetChange: (p) => propsRef.current.onSplitViewPresetChange?.(p),
      },
    )
    controllerRef.current = controller
    controller.setOnContextLost(() => propsRef.current.onContextLost?.())
    props.onGyroAvailabilityChange?.(controller.hasGyroscopeAvailable())
    props.setWrapper?.(containerRef.current)

    if (rotationAdjustment) controller.setRotationAdjustment(rotationAdjustment)
    // The constructor has just applied the `default` preset, so this lands
    // before the first user-visible frame. A missing half defaults to North /
    // the horizon rather than being read back off the camera.
    if (initialCamera && (Number.isFinite(initialCamera.yaw) || Number.isFinite(initialCamera.pitch))) {
      controller.setCamera(initialCamera.yaw ?? 0, initialCamera.pitch ?? 0)
    }

    // iOS motion-permission probe. Orientation events arriving unprompted
    // mean permission is already held, so motion comes on as it does on
    // Android. Otherwise nothing is asked here: the prompt belongs to the
    // Motion row in the view menu (toggleGyro above), not to page load.
    // probeTimeout and probe are declared here (effect scope) so the cleanup
    // closure can always cancel them, even when needsPermissionRequest() is false.
    let probeTimeout: ReturnType<typeof setTimeout> | undefined
    const probe = () => {
      motionGrantedRef.current = true
      window.removeEventListener('deviceorientation', probe)
      if (probeTimeout !== undefined) clearTimeout(probeTimeout)
      controller.enableGyro()
    }

    if (propsRef.current.interactive !== false && VideoPlayer360Controller.needsPermissionRequest()) {
      window.addEventListener('deviceorientation', probe)
      probeTimeout = setTimeout(() => window.removeEventListener('deviceorientation', probe), 400)
    }

    return () => {
      if (probeTimeout !== undefined) clearTimeout(probeTimeout)
      window.removeEventListener('deviceorientation', probe)
      controller.dispose()
      controllerRef.current = null
    }
  }, [core])

  useEffect(() => {
    controllerRef.current?.setRotationAdjustment(rotationAdjustment || 0)
  }, [rotationAdjustment])

  const interactive = props.interactive !== false
  useEffect(() => {
    controllerRef.current?.setInputEnabled(interactive)
  }, [interactive, core])

  // Before the layout effect below, so locked panes open already framed. Keyed
  // on the values, like the headings.
  const fourViewAspect = fourView?.paneAspect
  const fourViewZoom = fourView?.zoom
  useEffect(() => {
    if (fourViewAspect === undefined || fourViewZoom === undefined) return
    controllerRef.current?.setFourView({ paneAspect: fourViewAspect, zoom: fourViewZoom })
  }, [fourViewAspect, fourViewZoom, core])

  // `core` is a dependency because a new core means a new controller.
  // Keyed on the headings' values: a new array with the same headings in it
  // must not re-aim the panes.
  const headingsKey = (paneHeadings ?? []).join(',')
  const { count: paneCount, locked } = layout
  useEffect(() => {
    controllerRef.current?.setLayout({ count: paneCount, locked }, headingsKey ? headingsKey.split(',').map(Number) : [])
  }, [paneCount, locked, headingsKey, core])

  return (
    <div
      ref={containerRef}
      style={(fillHeight) ? styles.canvasContainerFill : compact ? styles.canvasContainerCompact : styles.canvasContainer}
    >
      {showIOSPermission && (
        <div style={styles.iosPermissionOverlay}>
          <div style={styles.iosPermissionContent}>
            <div style={styles.iosPermissionTitle}>Enable Motion Controls</div>
            <div style={styles.iosPermissionText}>
              This 360° video player uses your device's motion sensors. Tilt and rotate your device to look around.
            </div>
            <button
              style={styles.iosPermissionButton}
              onClick={async () => {
                const result = await controllerRef.current?.requestMotionPermission()
                setShowIOSPermission(false)
                if (result === 'granted') motionGrantedRef.current = true
                if (result === 'denied') {
                  setIOSPermissionDenied(true)
                  props.onGyroAvailabilityChange?.(false)
                }
              }}
            >
              Enable Motion Controls
            </button>
            <button style={styles.iosPermissionSkip} onClick={() => setShowIOSPermission(false)}>
              Not now
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

Sphere360Surface.displayName = 'Sphere360Surface'
export default Sphere360Surface
